import React, { useState, useEffect } from "react";
import {
  Loader,
  Table,
  Dropdown,
  Icon,
  Button,
  Message,
  Popup,
  Modal,
  Input,
  Checkbox,
} from "semantic-ui-react";
import { Waterform } from "../../../../api";
import { useAuth } from "../../../../hooks";
import {
  hasPermission,
  isAdmin,
  isMaster,
  isEditor,
  isReviewer,
} from "../../../../utils/checkPermission";
import "./ListWaterForms.scss";
import { BasicDrawer, ConfirmModal } from "../../../Shared";
import { PERIODS, normalizeUnit } from "../../../../utils";
import { formatDateView } from "../../../../utils/formatDate";
import { WaterForm } from "../WaterForm/WaterForm";
//import { waterCodes } from "../../../../utils/codes";
import { useLanguage } from "../../../../contexts";
import convert from "convert-units";
import {
  convertUnitsFinal,
  convertUnitsWater,
  determineWaterUnits,
  waterCodes,
} from "../../../../utils/dataAndFormulas/waterForms";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { UnitsForm } from "../UnitsForm";
import { UpdateUnitsModal } from "../UpdateUnitsModal";
import { Form } from "../../../../api";
const _ = require("lodash");

const waterFormController = new Waterform();
const formController = new Form();

// Multi-medidor: clave normalizada para agrupar por nombre de medidor sin que
// mayúsculas/espacios generen medidores "distintos" por error de tipeo.
const normalizeMeterKey = (name) => (name || "").trim().toLowerCase();

// Multi-medidor: pares consumo/costo que deberían tener los mismos medidores
// cargados para un mismo mes (para el aviso de inconsistencia).
const METER_FIELD_PAIRS = {
  water_domestic_municipal_network: "water_cost_domestic_municipal_network",
  water_cost_domestic_municipal_network: "water_domestic_municipal_network",
  municipal_network_water_production: "water_cost_municipal_network_production",
  water_cost_municipal_network_production: "municipal_network_water_production",
  combined_municipal_network_water: "water_cost_combined_municipal_network",
  water_cost_combined_municipal_network: "combined_municipal_network_water",
  domestic_groundwater: "domestic_groundwater_cost",
  domestic_groundwater_cost: "domestic_groundwater",
  groundwater_production: "groundwater_production_cost",
  groundwater_production_cost: "groundwater_production",
  groundwater_combined: "groundwater_combined_cost",
  groundwater_combined_cost: "groundwater_combined",
};

// Multi-medidor: campos que realmente soportan records[] en el modelo de
// Mongo (ver MULTI_METER_FIELDS en server/controllers/water.js — debe
// mantenerse igual a esa lista). Para cualquier otro campo (ej. las
// variantes "Doméstica"/"Producción" sueltas, sin "Combinada"), el schema
// no tiene declarado `records`, así que Mongo lo descarta en silencio: se
// puede "agregar un medidor", el total sube, pero el registro desaparece
// al volver a entrar. Por eso el modal de medidores solo se ofrece acá.
const MULTI_METER_FIELDS = [
  "water_domestic_municipal_network", "municipal_network_water_production", "combined_municipal_network_water",
  "water_cost_domestic_municipal_network", "water_cost_municipal_network_production", "water_cost_combined_municipal_network",
  "domestic_groundwater", "groundwater_production", "groundwater_combined",
  "domestic_groundwater_cost", "groundwater_production_cost", "groundwater_combined_cost",
  "rainwater", "surface_water", "cost_surface_water", "distilled_water",
  "reused_water", "recycled_water", "steam_condensed_water",
];

export function ListWaterForms(props) {
  const { reload, onReload, siteSelected, yearSelected, configFormData } =
    props;

  const [waterForms, setWaterForms] = useState([]);
  const [loading, setLoading] = useState(false);

  const { user, accessToken } = useAuth();

  const { language, translations } = useLanguage();

  const t = (key) => translations[key] || key; // Función para obtener la traducción

  useEffect(() => {
    (async () => {
      if (yearSelected !== "" && siteSelected !== undefined) {
        setLoading(true);
        try {
          const response = await waterFormController.getWaterFormsBySiteAndYear(
            accessToken,
            siteSelected,
            yearSelected,
          );
          if (response.code === 200) {
            setWaterForms(response.waterForms);
          } else {
            setWaterForms([]);
          }
        } catch (error) {
          console.error(error);
          setWaterForms([]);
        } finally {
          setLoading(false);
        }
      }
    })();
  }, [yearSelected, siteSelected, reload]);

  if (loading)
    return (
      <Loader active inline="centered" content="Cargando datos de agua..." />
    );
  //if (size(waterForms) === 0) return "No hay resultados ";

  // Actualiza un formulario ya cargado en memoria sin disparar onReload().
  // onReload() cambia la prop `reload`, lo que reactiva el useEffect de arriba
  // y pone loading=true — eso desmonta <TablePeriods> (reemplazado por el
  // <Loader>) y con él se pierde cualquier estado local, como el modal de
  // registros de medidor abierto. Para poder guardar una edición sin cerrar
  // ese modal, actualizamos `waterForms` directamente en memoria.
  const patchWaterForm = (itemId, field, fieldPayload) => {
    setWaterForms((prev) =>
      prev.map((f) => (f._id === itemId ? { ...f, [field]: fieldPayload } : f))
    );
  };

  return (
    <div className="list-water-forms">
      <TablePeriods
        data={waterForms}
        reload={reload}
        onReload={onReload}
        onPatchItem={patchWaterForm}
        accessToken={accessToken}
        year={yearSelected}
        site={siteSelected}
        t={t}
        configFormData={configFormData}
        language={language}
        user={user}
      />
    </div>
  );
}

function TablePeriods(props) {
  const {
    data,
    reload,
    onReload,
    onPatchItem,
    accessToken,
    year,
    site,
    t,
    configFormData,
    language,
    user,
  } = props;

  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState("");
  const [confirmContent, setConfirmContent] = useState("");
  const [dataDeleted, setDataDelete] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [uniqueFields, setUniqueFields] = useState([]);
  const [unitsRecorded, setUnitsRecorded] = useState([]);
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [expandedFields, setExpandedFields] = useState({});
  const [viewByMeterFields, setViewByMeterFields] = useState({});
  const [meterPopup, setMeterPopup] = useState(null); // { field, period, item } | null

  // Estado para controlar el modal
  const [selectedPeriod, setSelectedPeriod] = useState(null);

  // 🆕 Estados para edición batch
  const [editMode, setEditMode] = useState(false);
  const [editedValues, setEditedValues] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Estado para el modal de unidades
  const [showUnitsModal, setShowUnitsModal] = useState(false);
  const [showUnitsWarning, setShowUnitsWarning] = useState(false);
  const [showMissingUnitsWarning, setShowMissingUnitsWarning] = useState(false);

  // Estado para el modal de modificar unidades en formularios existentes
  const [showUpdateUnitsModal, setShowUpdateUnitsModal] = useState(false);
  const [isUpdatingUnits, setIsUpdatingUnits] = useState(false);

  // Estado para el modal de advertencia de unidades
  const [showWarningModal, setShowWarningModal] = useState(false);

  // Estado para el modal de advertencia de cambio de unidades (l → L)
  const [showLegacyUnitWarning, setShowLegacyUnitWarning] = useState(false);
  const [pendingUnitsToSave, setPendingUnitsToSave] = useState(null);

  const onOpenCloseModal = () => setShowModal((prevState) => !prevState);
  const onOpenCloseUnitsModal = () =>
    setShowUnitsModal((prevState) => !prevState);
  const onOpenCloseConfirm = () => setShowConfirm((prevState) => !prevState);
  const onOpenCloseUpdateUnitsModal = () =>
    setShowUpdateUnitsModal((prevState) => !prevState);

  useEffect(() => {
    (async () => {
      try {
        //Buscar

        if (site && year) {
          const fields = determineFields();

          setUniqueFields(fields);
        }
      } catch (error) {
        //console.error(error);
        setUniqueFields([]);
      }
    })();
  }, [configFormData, reload]);

  useEffect(() => {
    if (!uniqueFields?.length) return;

    const fetchUnits = async () => {
      try {
        let recordedUnits = {};
        let unitsFromConfig = false;

        // 1. Primero intentar obtener las unidades desde configFormData.fields (Form)
        if (configFormData?.fields && configFormData.fields.length > 0) {
          configFormData.fields.forEach((field) => {
            if (field.unit && field.name) {
              recordedUnits[field.name] = field.unit;
            }
          });
          if (Object.keys(recordedUnits).length > 0) {
            unitsFromConfig = true;
          }
        }

        // 2. Si no hay unidades en configFormData, obtener de los formularios guardados
        // COMENTADO: Esta lógica genera confusión. Ahora solo se usan las unidades de configForm.fields
        // if (Object.keys(recordedUnits).length === 0) {
        //   const response = await waterFormController.getLastUnitFieldsByYear(
        //     accessToken,
        //     site,
        //     year,
        //     uniqueFields
        //   );

        //   if (response.code === 200) {
        //     recordedUnits = response.fields;
        //   }
        // }

        setUnitsRecorded(recordedUnits);

        // 3. Construir selectedUnits basándose solo en lo registrado (sin defaults)
        const values = {};
        uniqueFields.forEach((field) => {
          // Solo usar la unidad si está registrada, no asignar valor por defecto
          values[field] = recordedUnits[field] || "";
        });

        setSelectedUnits(values);

        // Mostrar advertencia solo si no hay configFormData configurado y hay unidades de formularios
        setShowUnitsWarning(
          !configFormData && Object.keys(recordedUnits).length > 0,
        );

        // 4. Validar campos activos sin unidades en configFormData
        if (configFormData?.fields && configFormData.fields.length > 0) {
          const activeFieldsWithoutUnit = configFormData.fields.filter(
            (field) => field.active && !field.unit,
          );

          if (activeFieldsWithoutUnit.length > 0) {
            setShowMissingUnitsWarning(true);
          } else {
            setShowMissingUnitsWarning(false);
          }
        } else {
          setShowMissingUnitsWarning(false);
        }
      } catch (error) {
        console.error(error);
        setUnitsRecorded({});
        // Si falla, dejar todos los campos vacíos
        const values = {};
        uniqueFields.forEach((field) => {
          values[field] = "";
        });
        setSelectedUnits(values);
      }
    };

    fetchUnits();
  }, [uniqueFields, configFormData, reload]);

  // 🆕 Alerta al salir con cambios sin guardar
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasChanges]);

  // 🆕 Atajos de teclado
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.ctrlKey && e.key === "s") {
        e.preventDefault();
        if (hasChanges) {
          handleSaveChanges();
        }
      }
      if (e.key === "Escape" && editMode) {
        handleCancelEdit();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [editMode, hasChanges]);

  // Función para abrir el modal con datos del período seleccionado
  const handleOpenModal = (period) => {
    setSelectedPeriod(data.find((item) => item.period === period));
    //setModalOpen(true);
    onOpenCloseModal();
  };

  // Función para cerrar el modal
  const handleDeleteModal = (period) => {
    const form = data.find((item) => item.period === period);
    setConfirmContent(
      `${t("delete_dated_water_form")} ${formatDateView(form.date)}`,
    );
    setDataDelete(form);
    onOpenCloseConfirm();
  };

  const openUpdateEffluentForm = (period) => {
    //setFieldName(name);
    const form = data.find((item) => item.period === period);
    setModalContent(
      <WaterForm
        onClose={onOpenCloseModal}
        onReload={onReload}
        waterForm={form}
        unitsRecorded={unitsRecorded}
        configFormData={configFormData}
      />,
    );
    setShowModal(true);
  };

  // Función para verificar si hay al menos una unidad configurada
  const validateUnitsConfigured = () => {
    // Si no hay configFormData o no hay fields, permitir continuar (usará campos por defecto)
    if (!configFormData?.fields || configFormData.fields.length === 0) {
      return { valid: true };
    }

    // Filtrar solo los campos activos
    const activeFields = configFormData.fields.filter(
      (field) => field.active !== false,
    );

    if (activeFields.length === 0) {
      return { valid: false, message: t("no_active_fields") };
    }

    // Verificar que haya al menos UNA unidad configurada
    const fieldsWithUnit = activeFields.filter(
      (field) => field.unit && field.unit !== "",
    );

    if (fieldsWithUnit.length === 0) {
      return {
        valid: false,
        message:
          t("units_not_configured") ||
          "Debe configurar al menos una unidad antes de agregar un periodo. Haga clic en el ícono de tres puntos (⋯) en la columna de Unidades.",
      };
    }

    return { valid: true };
  };

  const openNewWaterForm = (period) => {
    // Validar que las unidades estén configuradas antes de abrir el formulario
    const validation = validateUnitsConfigured();
    if (!validation.valid) {
      toast.error(validation.message);
      return;
    }

    setModalContent(
      <WaterForm
        onClose={onOpenCloseModal}
        onReload={onReload}
        period={period}
        year={year}
        siteSelected={site}
        unitsRecorded={unitsRecorded}
        configFormData={configFormData}
      />,
    );
    setShowModal(true);
  };

  const handleUnitsFormSave = (units) => {
    const hasExistingUnits = configFormData?.fields?.some(f => f.unit);
    if (hasExistingUnits) {
      setPendingUnitsToSave(units);
      setShowLegacyUnitWarning(true);
    } else {
      handleSaveUnits(units);
    }
  };

  // Función para guardar unidades en el Form
  const handleSaveUnits = async (units) => {
    try {
      const formId = configFormData?._id;
      const currentFields = configFormData?.fields || [];

      // Verificar si hay cambios en las unidades y si existen datos históricos
      const hasUnitChanges = currentFields.some((field) => {
        const newUnit = units[field.name];
        return newUnit && field.unit && newUnit !== field.unit;
      });

      if (hasUnitChanges && data && data.length > 0) {
        // Mostrar advertencia si hay datos existentes y cambios de unidades
        const changedFields = currentFields
          .filter((field) => {
            const newUnit = units[field.name];
            return newUnit && field.unit && newUnit !== field.unit;
          })
          .map(
            (field) =>
              `\n  • ${t(field.name)}: ${field.unit} → ${units[field.name]}`,
          );

        const confirmed = window.confirm(
          `⚠️ ADVERTENCIA: Cambio de unidades detectado\n\n` +
            `Los siguientes campos cambiarán de unidad:${changedFields.join("")}\n\n` +
            `IMPORTANTE:\n` +
            `✓ Los datos históricos mantendrán sus unidades originales\n` +
            `✓ Solo los NUEVOS períodos usarán las nuevas unidades\n` +
            `✓ Los dashboards convertirán automáticamente para comparar\n` +
            `✓ Cada formulario guarda su propia unidad (historial inmutable)\n\n` +
            `¿Deseas continuar con el cambio de unidades?`,
        );

        if (!confirmed) return;
      }

      if (!formId || currentFields.length === 0) {
        // No hay Form configurado, crear uno nuevo con todos los campos de water
        const allWaterFieldsData = [
          { name: "water_domestic_municipal_network", code: "Agu1" },
          { name: "municipal_network_water_production", code: "Agu2" },
          { name: "combined_municipal_network_water", code: "Agu3" },
          { name: "water_cost_domestic_municipal_network", code: "Agu4" },
          { name: "water_cost_municipal_network_production", code: "Agu5" },
          { name: "water_cost_combined_municipal_network", code: "Agu6" },
          { name: "domestic_groundwater", code: "Agu7" },
          { name: "groundwater_production", code: "Agu8" },
          { name: "groundwater_combined", code: "Agu9" },
          { name: "domestic_groundwater_cost", code: "Agu10" },
          { name: "groundwater_production_cost", code: "Agu11" },
          { name: "groundwater_combined_cost", code: "Agu12" },
          { name: "rainwater", code: "Agu13" },
          { name: "surface_water", code: "Agu14" },
          { name: "cost_surface_water", code: "Agu15" },
          { name: "reused_water", code: "Agu16" },
          { name: "recycled_water", code: "Agu17" },
          { name: "steam_condensed_water", code: "Agu18" },
        ];

        const fieldsToCreate = allWaterFieldsData.map((field) => ({
          code: field.code,
          name: field.name,
          active: true,
          unit: units[field.name] || "",
          standardized_coefficient: 1,
        }));

        const newFormData = {
          module: "water",
          site: site,
          fields: fieldsToCreate,
        };

        const response = await formController.createForm(
          accessToken,
          newFormData,
        );
        if (response.code === 200 || response._id) {
          toast.success(t("units_updated_successfully"));
          setShowUnitsWarning(false); // Ocultar advertencia
          onReload();
        } else {
          toast.error(t("error_updating_units"));
        }
      } else {
        // Ya existe un Form: actualizar solo las unidades que cambian, de
        // forma atómica (ver updateFormFields en el controlador) — no se
        // pisa el documento entero para no perder un toggle de 'active'
        // guardado desde Fuentes casi al mismo tiempo.
        const changes = currentFields
          .filter((field) => Object.prototype.hasOwnProperty.call(units, field.name))
          .map((field) => ({ name: field.name, unit: units[field.name] }));

        const response =
          changes.length > 0
            ? await formController.updateFormFields(accessToken, formId, changes)
            : { code: 200 };

        if (response.code === 200) {
          toast.success(t("units_updated_successfully"));
          setShowUnitsWarning(false); // Ocultar advertencia
          onReload();
        } else {
          toast.error(t("error_updating_units"));
        }
      }
    } catch (error) {
      console.error(error);
      toast.error(t("error_updating_units"));
    }
  };

  // Función para aplicar unidades a formularios existentes (modal separado)
  const handleApplyUnitsToForms = async (unitsToApply) => {
    if (!unitsToApply || Object.keys(unitsToApply).length === 0) {
      toast.warning(t("no_fields_selected") || "No hay campos seleccionados");
      return;
    }

    setIsUpdatingUnits(true);
    try {
      const response = await waterFormController.updateUnitsForYear(
        accessToken,
        site,
        year,
        unitsToApply,
      );

      if (response.code === 200) {
        toast.success(
          `${t("units_applied_to_forms") || "Unidades aplicadas a"} ${response.updated} ${t("forms") || "formularios"}`,
        );
        onOpenCloseUpdateUnitsModal();
        onReload();
      } else {
        toast.error(
          t("error_applying_units_to_forms") || "Error al aplicar unidades",
        );
      }
    } catch (error) {
      console.error("Error applying units:", error);
      toast.error(
        t("error_applying_units_to_forms") || "Error al aplicar unidades",
      );
    } finally {
      setIsUpdatingUnits(false);
    }
  };

  const onDelete = async () => {
    try {
      //TODO: modificar por controlador correspondiente
      const response = await waterFormController.deleteWaterForm(
        accessToken,
        dataDeleted._id,
      );
      if (response.code && response.code === 200) {
        toast.success("Formulario eliminado correctamente!.");
      }
      setDataDelete("");
      onReload();
      onOpenCloseConfirm();
    } catch (error) {
      console.error(error);
    }
  };

  // Obtén los nombres de los períodos para las columnas
  const periods = PERIODS;

  // Determinar los campos únicos y si tienen código
  const determineFields = () => {
    const fields = new Set();

    if (
      configFormData &&
      configFormData.fields &&
      configFormData.fields.length > 0
    ) {
      Object.keys(waterCodes).forEach((key) => {
        if (
          configFormData.fields &&
          configFormData.fields.some(
            (field) => field.name === key && field.active,
          )
        ) {
          fields.add(key);
        }
      });
    } else {
      Object.keys(waterCodes).forEach((key) => {
        fields.add(key);
      });
    }
    return Array.from(fields);
  };

  // Retorna el código
  const hasCode = (field) => {
    return waterCodes[field];
  };

  const hasDataPeriod = (period) => {
    const existsPeriod = data.some((entry) => entry.period === period);
    return existsPeriod;
  };

  // Multi-medidor: cantidad de medidores distintos que aportan a un campo en el año
  // (agrupados por nombre normalizado — "Medidor A" y "medidor a " cuentan como uno solo)
  const getDistinctMeterCount = (field) => {
    const keys = new Set();
    data.forEach((item) => {
      (item[field]?.records || []).forEach((r) => keys.add(normalizeMeterKey(r.meterName)));
    });
    return keys.size;
  };

  // Multi-medidor: desglose {meterName: {period: value}} para las sub-filas.
  // Se agrupa por nombre normalizado; se muestra el nombre tal como se escribió
  // la primera vez que se vio ese medidor.
  const getMeterBreakdown = (field) => {
    const byMeter = {};
    data.forEach((item) => {
      (item[field]?.records || []).forEach((r) => {
        const key = normalizeMeterKey(r.meterName);
        if (!byMeter[key]) byMeter[key] = { displayName: (r.meterName || "").trim(), monthly: {} };
        byMeter[key].monthly[item.period] = (byMeter[key].monthly[item.period] || 0) + (r.value || 0);
      });
    });
    return Object.values(byMeter).map(({ displayName, monthly }) => [displayName, monthly]);
  };

  // Multi-medidor: nombres de medidor ya usados en el año, sin importar el campo
  // (para ofrecerlos en el selector del modal de agregar registro). Deduplicados
  // por nombre normalizado, se ofrece el nombre tal como se escribió primero.
  const getAllMeterNames = () => {
    const seen = new Map();
    data.forEach((item) => {
      uniqueFields.forEach((field) => {
        (item[field]?.records || []).forEach((r) => {
          const key = normalizeMeterKey(r.meterName);
          if (key && !seen.has(key)) seen.set(key, (r.meterName || "").trim());
        });
      });
    });
    return Array.from(seen.values()).sort();
  };

  // Multi-medidor: ¿los medidores cargados en este campo para este mes no
  // coinciden con los del campo pareado (consumo <-> costo)? Solo avisa, no bloquea.
  const hasMeterMismatch = (field, period) => {
    const pairField = METER_FIELD_PAIRS[field];
    if (!pairField) return false;
    const item = data.find((d) => d.period === period);
    const namesA = new Set((item?.[field]?.records || []).map((r) => normalizeMeterKey(r.meterName)));
    const namesB = new Set((item?.[pairField]?.records || []).map((r) => normalizeMeterKey(r.meterName)));
    if (namesA.size === 0 && namesB.size === 0) return false;
    if (namesA.size !== namesB.size) return true;
    for (const key of namesA) if (!namesB.has(key)) return true;
    return false;
  };

  // Obtener todos los campos únicos

  // Función para formatear números sin decimales y con puntos en miles
  function formatNumberWithThousands(numero) {
    if (typeof numero !== "number" || isNaN(numero)) {
      return numero;
    }
    const entero = Math.round(numero);
    return entero.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  }

  // La conversión de unidades permite transformar valores entre diferentes unidades de medida (ej. litros a metros cúbicos).
  // Para mantener las unidades fijas en la tabla, no se convierten los valores; se muestran tal como están registrados.
  const calculateTotalAndAverage = (field) => {
    const values = data.map((item) => {
      return item[field]?.value || 0;
    });

    const totalFinal = values.reduce((ac, value) => ac + value, 0);
    const total = formatNumberWithThousands(totalFinal);
    const averageFinal = values.length ? totalFinal / values.length : 0;
    const average = formatNumberWithThousands(averageFinal);
    return { total, average };
  };

  const formatValue = (num, language) => {
    // const type = language && language === "en" ? "en-US" : "de-DE";
    // let formateado = new Intl.NumberFormat(type, {
    //   // minimumFractionDigits: 2,
    //   // maximumFractionDigits: 2,
    // }).format(num);

    // return formateado;
    return num;
  };

  const getUnitName = (field) => {
    const selectedUnitValue = normalizeUnit(selectedUnits[field]);

    // Si no hay unidad configurada, mostrar mensaje indicativo
    if (!selectedUnitValue || selectedUnitValue === "") {
      return "- - -";
    }

    const units = determineWaterUnits(field);
    const unitObject = units.find((u) => u.value === selectedUnitValue);
    return unitObject ? unitObject.name : selectedUnitValue;
  };

  const handleUnitChange = (field, data) => {
    setSelectedUnits({ ...selectedUnits, [field]: data.value });
  };

  // 🆕 Manejar cambio en celda editable
  const handleCellChange = (field, period, item, newValue) => {
    const key = `${period}_${field}`;
    const originalValue = item?.[field]?.value || null;

    // Si el valor es igual al original, remover del objeto de ediciones
    if (newValue === originalValue) {
      const updatedEdits = { ...editedValues };
      delete updatedEdits[key];
      setEditedValues(updatedEdits);
      setHasChanges(Object.keys(updatedEdits).length > 0);
      return;
    }

    // Agregar/actualizar edición
    setEditedValues({
      ...editedValues,
      [key]: {
        formId: item?._id || null,
        period: period,
        field: field,
        value: newValue,
        unit: selectedUnits[field],
        originalValue: originalValue,
        isNew: !item || !item._id,
        year: year,
        site: site,
      },
    });
    setHasChanges(true);
  };

  // 🆕 Guardar cambios (batch update)
  const handleSaveChanges = async () => {
    if (!hasChanges) return;

    try {
      setIsSaving(true);

      // Agrupar cambios por tipo: updates vs creates
      const updates = [];
      const creates = [];

      Object.values(editedValues).forEach((change) => {
        if (change.isNew) {
          creates.push({
            period: change.period,
            year: change.year,
            site: change.site,
            field: change.field,
            value: change.value,
            unit: change.unit,
            code: waterCodes[change.field], // Agregar código del campo
          });
        } else {
          updates.push({
            formId: change.formId,
            field: change.field,
            value: change.value,
            unit: change.unit,
            code: waterCodes[change.field], // Agregar código del campo
          });
        }
      });

      // Enviar batch update al backend
      const response = await waterFormController.batchUpdateWaterForms(
        accessToken,
        {
          updates,
          creates,
          site,
          year,
          creatorUser: user._id, // Agregar ID del usuario creador
        },
      );

      if (response.code === 200) {
        toast.success(
          `✅ ${response.updated} formularios actualizados, ${response.created} creados`,
        );
        setEditedValues({});
        setHasChanges(false);
        setEditMode(false);
        onReload();
      } else {
        toast.error("❌ Error al guardar los cambios");
      }
    } catch (error) {
      console.error("Error saving batch changes:", error);
      toast.error("❌ Error al guardar los cambios");
    } finally {
      setIsSaving(false);
    }
  };

  // 🆕 Cancelar edición
  const handleCancelEdit = () => {
    setEditMode(false);
    setEditedValues({});
    setHasChanges(false);
  };

  const toggleExpand = (field) => {
    setExpandedFields((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  function showWithTwoDecimals(numero) {
    // Verificamos si el parámetro es un número
    if (typeof numero !== "number" || isNaN(numero)) {
      // Si el valor no es un número, simplemente retornamos el valor tal como está.
      return numero;
    }

    // Si el número es entero, lo mostramos como entero.
    if (numero % 1 === 0) {
      return numero.toString(); // Muestra el número como cadena sin decimales.
    } else {
      // Si el número tiene decimales, lo mostramos con hasta 4 decimales
      // Usamos toFixed(2) para asegurar que no haya más de 4 decimales, pero luego
      // eliminamos los ceros innecesarios.
      let numeroConCuatroDecimales = numero.toFixed(2);

      // Eliminamos los ceros a la derecha si los hay
      return parseFloat(numeroConCuatroDecimales).toString();
    }
  }

  // Verificar si hay al menos un período cargado
  const hasLoadedData = data && data.length > 0;

  return (
    <>
      {/* Botones de control */}
      {(isMaster(user.role) || isAdmin(user.role) || isEditor(user.role)) && (
      <div className="batch-actions">
        {!editMode ? (
          <Popup
            content={t("enable_multiple_edit")}
            trigger={
              <Button
                className="batch-btn primary"
                icon
                //labelPosition="left"
                onClick={() => setEditMode(true)}
                disabled={!hasLoadedData}
              >
                <Icon name="edit" />
                {/* Editar Múltiples Períodos */}
              </Button>
            }
          />
        ) : (
          <>
            <Button
              className="batch-btn success"
              icon
              labelPosition="left"
              onClick={handleSaveChanges}
              disabled={!hasChanges || isSaving}
              loading={isSaving}
            >
              <Icon name="save" />
              Guardar Cambios
              {hasChanges && ` (${Object.keys(editedValues).length})`}
            </Button>
            <Button
              className="batch-btn cancel"
              icon
              labelPosition="left"
              onClick={handleCancelEdit}
              disabled={isSaving}
            >
              <Icon name="cancel" />
              Cancelar
            </Button>
            {hasChanges && (
              <span style={{ color: "#f57c00", fontWeight: "bold" }}>
                <Icon name="warning sign" />
                {Object.keys(editedValues).length} cambio(s) pendiente(s)
              </span>
            )}
          </>
        )}
      </div>
      )}

      {/* 🆕 Mensaje de cambios pendientes */}
      {hasChanges && (
        <Message warning>
          <Message.Header>Cambios sin guardar</Message.Header>
          <p>
            Tienes {Object.keys(editedValues).length} cambio(s) pendiente(s). No
            olvides guardar antes de salir. (Ctrl+S para guardar, Esc para
            cancelar)
          </p>
        </Message>
      )}

      {/* 🆕 Advertencia de unidades tomadas de formularios */}
      {showUnitsWarning && (
        <Message info>
          <Message.Header>Configuración de unidades recomendada</Message.Header>
          <p>
            Las unidades mostradas provienen de formularios guardados. Para una
            mejor consistencia, se recomienda configurar las unidades
            explícitamente haciendo clic en el ícono de tres puntos (⋯) en la
            columna "Unidades".
          </p>
        </Message>
      )}

      <div className="list-water-forms__table-wrapper">
        <Table className="water-table" unstackable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell className="sticky-col-1 pill-left-start">
                {t("code")}
              </Table.HeaderCell>
              <Table.HeaderCell className="sticky-col-2">
                {t("concept")}
              </Table.HeaderCell>
              <Table.HeaderCell className="pill-left-end">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                  }}
                >
                  {t("units")}
                  {showMissingUnitsWarning && (
                    <Icon
                      name="warning sign"
                      color="orange"
                      style={{ cursor: "pointer", margin: 0 }}
                      onClick={() => setShowWarningModal(true)}
                    />
                  )}
                  <Dropdown
                    icon="ellipsis vertical"
                    floating
                    className="icon"
                    style={{ cursor: "pointer" }}
                  >
                    <Dropdown.Menu>
                      <Dropdown.Item
                        text={t("configure_units") || "Configurar unidades"}
                        icon="setting"
                        onClick={onOpenCloseUnitsModal}
                      />
                      {data.length > 0 && (
                        <Dropdown.Item
                          text={
                            t("update_units_in_forms") ||
                            "Modificar unidades en formularios"
                          }
                          icon="edit"
                          onClick={onOpenCloseUpdateUnitsModal}
                        />
                      )}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </Table.HeaderCell>
              <Table.HeaderCell className="pill-gap" />
              <Table.HeaderCell
                colSpan="3"
                textAlign="center"
                className="pill-right"
              >
                {t("report_period")} {year}
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {uniqueFields
              ? uniqueFields.map((field) => {
                  const isExpanded = expandedFields[field];
                  const { total, average } = calculateTotalAndAverage(field);
                  const hasMismatchedUnit = data.some((item) => {
                const itemUnit = normalizeUnit(item?.[field]?.unit);
                const configuredUnit = normalizeUnit(selectedUnits[field]);
                return itemUnit && configuredUnit && itemUnit !== configuredUnit;
              });
                  const distinctMeters = getDistinctMeterCount(field);
                  const showByMeter = viewByMeterFields[field] !== false;
                  const isMeterCapableField = MULTI_METER_FIELDS.includes(field);

                  return (
                    <React.Fragment key={field}>
                      {/* Fila resumen */}
                      <Table.Row>
                        <Table.Cell className="sticky-col-1 pill-left-start">
                          {waterCodes[field]}
                        </Table.Cell>
                        <Table.Cell className="sticky-col-2">
                          {t(field)}
                          {distinctMeters > 1 && (
                            <span
                              style={{
                                marginLeft: "0.5em",
                                background: "#e8f4fd",
                                color: "#1a6ba0",
                                borderRadius: "12px",
                                padding: "1px 8px",
                                fontSize: "0.75em",
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                              }}
                            >
                              💧 {distinctMeters} {t("meters") || "medidores"}
                            </span>
                          )}
                        </Table.Cell>
                        <Table.Cell
                          textAlign="center"
                          className="pill-left-end"
                        >
                          {getUnitName(field)}
                          {hasMismatchedUnit && (
                            <Popup
                              content="Hay meses con unidades diferentes a la configurada"
                              trigger={
                                <Icon
                                  name="warning sign"
                                  color="orange"
                                  size="small"
                                  style={{ cursor: "pointer", marginLeft: "4px" }}
                                />
                              }
                            />
                          )}
                        </Table.Cell>
                        <Table.Cell className="pill-gap" />
                        <Table.Cell colSpan="3" className="pill-right">
                          <div className="summary-flex">
                            <span className="summary-item">
                              {t("total")}: <strong>{total}</strong>
                            </span>
                            <span className="summary-item">
                              {t("average")}: <strong>{average}</strong>
                            </span>
                            <Button
                              icon
                              size="tiny"
                              className="expand-btn"
                              onClick={() => toggleExpand(field)}
                            >
                              <Icon
                                name={
                                  isExpanded ? "chevron up" : "chevron down"
                                }
                              />
                            </Button>
                          </div>
                        </Table.Cell>
                      </Table.Row>

                      {/* Fila cabecera de meses (expandida) */}
                      {isExpanded && (
                        <Table.Row className="months-header-row">
                          <Table.Cell
                            colSpan="7"
                            style={{ padding: 0 }}
                            className="months-full-row"
                          >
                            <div className="months-flex-container">
                              {periods.map((period) => (
                                <div
                                  key={period}
                                  className="month-flex-cell month-flex-header"
                                >
                                  {t(period).slice(0, 3)}
                                  {!editMode &&
                                    (hasDataPeriod(period) ? (
                                      <Dropdown
                                        inline
                                        size="tiny"
                                        icon="ellipsis vertical"
                                        floating
                                        className="icon"
                                      >
                                        <Dropdown.Menu>
                                          <Dropdown.Item
                                            text={t("edit")}
                                            icon={
                                              <Icon
                                                name="edit"
                                                style={{ color: "gray" }}
                                              />
                                            }
                                            onClick={() =>
                                              openUpdateEffluentForm(period)
                                            }
                                          />
                                          <Dropdown.Item
                                            text={t("delete")}
                                            icon={
                                              <Icon
                                                name="trash"
                                                style={{ color: "red" }}
                                              />
                                            }
                                            onClick={() =>
                                              handleDeleteModal(period)
                                            }
                                          />
                                        </Dropdown.Menu>
                                      </Dropdown>
                                    ) : (
                                      <Dropdown
                                        link
                                        size="tiny"
                                        icon="ellipsis vertical"
                                        floating
                                        className="icon"
                                      >
                                        <Dropdown.Menu>
                                          {(isAdmin(user.role) ||
                                            isEditor(user.role) ||
                                            isMaster(user.role)) && (
                                            <Dropdown.Item
                                              text={t("load_data")}
                                              icon="plus"
                                              onClick={() =>
                                                openNewWaterForm(period)
                                              }
                                            />
                                          )}
                                        </Dropdown.Menu>
                                      </Dropdown>
                                    ))}
                                </div>
                              ))}
                            </div>
                          </Table.Cell>
                        </Table.Row>
                      )}

                      {/* Fila datos de meses (expandida) */}
                      {isExpanded && (
                        <Table.Row className="months-data-row">
                          <Table.Cell
                            colSpan="7"
                            style={{ padding: 0 }}
                            className="months-full-row"
                          >
                            <div className="months-flex-container">
                              {periods.map((period) => {
                                const item = data.find(
                                  (d) => d.period === period,
                                );
                                const editKey = `${period}_${field}`;
                                const isEdited =
                                  editedValues[editKey] !== undefined;
                                const hasUnit =
                                  selectedUnits[field] &&
                                  selectedUnits[field] !== "";
                                const itemUnit = normalizeUnit(item?.[field]?.unit);
                                const configuredUnit = normalizeUnit(selectedUnits[field]);
                                const isDifferentUnit =
                                  itemUnit &&
                                  configuredUnit &&
                                  itemUnit !== configuredUnit;
                                let cellContent;

                                if (editMode) {
                                  const displayVal = isEdited
                                    ? editedValues[editKey].value
                                    : (item?.[field]?.value ?? "");
                                  cellContent = (
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={displayVal}
                                      onChange={(e) =>
                                        handleCellChange(
                                          field,
                                          period,
                                          item,
                                          parseFloat(e.target.value) || 0,
                                        )
                                      }
                                      disabled={!hasUnit || isReviewer(user.role)}
                                      style={{
                                        width: "100%",
                                        height: "34px",
                                        border: isEdited
                                          ? "2px solid #fbc02d"
                                          : "1px solid #ccc",
                                        borderRadius: "4px",
                                        padding: "4px 8px",
                                        textAlign: "right",
                                        fontSize: "13px",
                                        background: hasUnit
                                          ? "white"
                                          : "#f5f5f5",
                                        color: !hasUnit ? "#999" : isReviewer(user.role) ? "#000" : "inherit",
                                        MozAppearance: "textfield",
                                      }}
                                      placeholder="-"
                                    />
                                  );
                                } else {
                                  const recordCount = item?.[field]?.records?.length || 0;
                                  cellContent = item?.[field] ? (
                                    <span>
                                      {formatNumberWithThousands(
                                        item[field].value,
                                      )}
                                      {isDifferentUnit && (
                                        <span
                                          style={{
                                            fontSize: "0.75em",
                                            color: "#f57c00",
                                            marginLeft: "2px",
                                          }}
                                          title={`Unidad: ${itemUnit} | Configurada: ${configuredUnit}`}
                                        >
                                          ({itemUnit})
                                        </span>
                                      )}
                                      {recordCount > 1 && (
                                        <span
                                          style={{
                                            marginLeft: "0.3em", background: "#1a2a4a", color: "#fff",
                                            borderRadius: "50%", padding: "0 5px", fontSize: "0.7em",
                                          }}
                                        >
                                          {recordCount}
                                        </span>
                                      )}
                                      {hasMeterMismatch(field, period) && (
                                        <Popup
                                          content={t("meter_mismatch_warning") || "Los medidores cargados acá no coinciden con los del campo de consumo/costo pareado para este mes."}
                                          trigger={
                                            <Icon
                                              name="warning sign"
                                              color="orange"
                                              size="small"
                                              style={{ marginLeft: "0.3em", cursor: "pointer" }}
                                            />
                                          }
                                        />
                                      )}
                                    </span>
                                  ) : (
                                    "-"
                                  );
                                }

                                return (
                                  <div
                                    key={period}
                                    className="month-flex-cell month-flex-data"
                                    style={!editMode && isMeterCapableField ? { cursor: "pointer" } : undefined}
                                    title={!editMode && isMeterCapableField ? (t("view_add_meter_records") || "Ver / agregar registros del mes") : undefined}
                                    onClick={
                                      !editMode && isMeterCapableField
                                        ? () => setMeterPopup({ field, period, item: item || null })
                                        : undefined
                                    }
                                  >
                                    {cellContent}
                                  </div>
                                );
                              })}
                            </div>
                          </Table.Cell>
                        </Table.Row>
                      )}

                      {/* Multi-medidor: toggle + sub-filas por medidor (expandida) */}
                      {isExpanded && distinctMeters > 1 && (
                        <>
                          <Table.Row className="months-header-row">
                            <Table.Cell className="sticky-col-1" style={{ padding: "4px 8px" }}>
                              <Checkbox
                                toggle
                                label={t("view_by_meter") || "Ver por medidor"}
                                checked={showByMeter}
                                onChange={(_, d) =>
                                  setViewByMeterFields((prev) => ({ ...prev, [field]: d.checked }))
                                }
                              />
                            </Table.Cell>
                            <Table.Cell colSpan="6" />
                          </Table.Row>
                          {showByMeter &&
                            getMeterBreakdown(field).map(([meterName, monthly]) => (
                              <Table.Row key={meterName} className="months-data-row">
                                <Table.Cell colSpan="7" style={{ padding: 0 }} className="months-full-row">
                                  <div style={{ padding: "4px 12px", fontSize: "0.95em", color: "#648EBC", fontWeight: 600 }}>
                                    <Icon name="tint" /> {meterName}
                                  </div>
                                  <div className="months-flex-container">
                                    {periods.map((period) => (
                                      <div key={period} className="month-flex-cell month-flex-data" style={{ color: "#888" }}>
                                        {monthly[period] != null ? formatNumberWithThousands(monthly[period]) : "-"}
                                      </div>
                                    ))}
                                  </div>
                                </Table.Cell>
                              </Table.Row>
                            ))}
                        </>
                      )}
                    </React.Fragment>
                  );
                })
              : null}
          </Table.Body>
        </Table>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={1200}
        hideProgressBar
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      <BasicDrawer show={showModal} close={onOpenCloseModal}>
        {modalContent}
      </BasicDrawer>
      <ConfirmModal
        open={showConfirm}
        title={t("delete")}
        content={confirmContent}
        onConfirm={onDelete}
        onCancel={onOpenCloseConfirm}
        danger
      />

      <UnitsForm
        show={showUnitsModal}
        onClose={onOpenCloseUnitsModal}
        onSave={handleUnitsFormSave}
        currentUnits={unitsRecorded}
        registeredFields={configFormData?.fields || []}
      />

      <UpdateUnitsModal
        show={showUpdateUnitsModal}
        onClose={onOpenCloseUpdateUnitsModal}
        onApply={handleApplyUnitsToForms}
        currentUnits={unitsRecorded}
        registeredFields={configFormData?.fields || []}
        isLoading={isUpdatingUnits}
      />

      <ConfirmModal
        open={showWarningModal}
        icon={<Icon name="warning sign" color="orange" />}
        title="Unidades sin configurar"
        content={
          <>
            <p>Los campos no tienen unidades asignadas en la configuración.</p>
            <p>
              Por favor configure las unidades haciendo clic en el ícono{" "}
              <strong>⋯</strong> en la columna Unidades.
            </p>
          </>
        }
        onCancel={() => setShowWarningModal(false)}
        onConfirm={() => {
          setShowWarningModal(false);
          onOpenCloseUnitsModal();
        }}
        cancelText="Cerrar"
        confirmText="Configurar unidades"
      />

      <ConfirmModal
        open={showLegacyUnitWarning}
        icon={<Icon name="warning sign" color="orange" />}
        title="Cambio de unidades"
        content={
          <>
            <p>
              Los registros anteriores continuarán guardados con la unidad{" "}
              <strong>"l"</strong> (litros). A partir de ahora, los nuevos
              datos se guardarán con <strong>"L"</strong> (Litros).
            </p>
            <p>¿Desea continuar con el cambio?</p>
          </>
        }
        onCancel={() => setShowLegacyUnitWarning(false)}
        onConfirm={() => {
          setShowLegacyUnitWarning(false);
          handleSaveUnits(pendingUnitsToSave);
        }}
        cancelText="Cancelar"
        confirmText="Confirmar"
      />

      {meterPopup && (
        <MeterRecordsModal
          meterPopup={meterPopup}
          onClose={() => setMeterPopup(null)}
          accessToken={accessToken}
          site={site}
          year={year}
          user={user}
          t={t}
          existingMeterNames={getAllMeterNames()}
          canEdit={isAdmin(user.role) || isEditor(user.role) || isMaster(user.role)}
          onSaved={() => {
            setMeterPopup(null);
            onReload();
          }}
          onSavedKeepOpen={(fieldPayload) => {
            setMeterPopup((prev) =>
              prev ? { ...prev, item: { ...(prev.item || {}), [prev.field]: fieldPayload } } : prev
            );
            // A propósito NO llamamos a onReload() acá: eso pondría loading=true
            // en el componente padre y desmontaría este modal. En cambio,
            // actualizamos el registro en memoria para que la tabla de fondo
            // (totales, badge de medidores) quede sincronizada sin cerrar el modal.
            if (meterPopup?.item?._id) {
              onPatchItem(meterPopup.item._id, meterPopup.field, fieldPayload);
            }
          }}
        />
      )}
    </>
  );
}

// Multi-medidor: modal para ver los registros de un mes/campo y agregar uno nuevo a mano.
function MeterRecordsModal({ meterPopup, onClose, accessToken, site, year, user, t, existingMeterNames = [], canEdit = true, onSaved, onSavedKeepOpen }) {
  const { field, period, item } = meterPopup;
  const [meterName, setMeterName] = useState("");
  const [meterOptions, setMeterOptions] = useState(existingMeterNames);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  // null = agregando un registro nuevo; número = editando existingRecords[editingIndex]
  const [editingIndex, setEditingIndex] = useState(null);

  const existingRecords = item?.[field]?.records || [];
  const currentTotal = item?.[field]?.value;
  const currentUnit = item?.[field]?.unit;

  // Medidor cargado en el campo pareado (consumo <-> costo) que todavía no
  // está cargado acá — mismo criterio que hasMeterMismatch, pero mostrando
  // el/los nombres puntuales para que se puedan completar sin salir del modal.
  const pairField = METER_FIELD_PAIRS[field];
  const pairRecords = item?.[pairField]?.records || [];
  const ownKeys = new Set(existingRecords.map((r) => normalizeMeterKey(r.meterName)));
  const missingHere = pairRecords.filter((r) => !ownKeys.has(normalizeMeterKey(r.meterName)));

  const resetForm = () => {
    setMeterName("");
    setValue("");
    setEditingIndex(null);
  };

  const handleStartEdit = (i) => {
    if (!canEdit) return;
    setMeterName(existingRecords[i].meterName);
    setValue(String(existingRecords[i].value));
    setEditingIndex(i);
  };

  const handleDelete = async (i) => {
    if (!canEdit || !item?._id) return;
    setSaving(true);
    try {
      const newRecords = existingRecords.filter((_, idx) => idx !== i);
      const newTotal = newRecords.reduce((s, r) => s + (r.value || 0), 0);
      await waterFormController.updateWaterForm(accessToken, item._id, { [field]: { records: newRecords, value: newTotal } });
      toast.success(t("meter_record_deleted") || "Registro eliminado");
      if (editingIndex === i) resetForm();
      onSaved();
    } catch (error) {
      console.error(error);
      toast.error(t("error_saving_meter_record") || "Error al guardar el registro");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!canEdit) return;
    const numValue = parseFloat(value);
    if (!meterName.trim() || isNaN(numValue)) {
      toast.warning(t("meter_name_and_value_required") || "Completá nombre de medidor y valor");
      return;
    }
    setSaving(true);
    try {
      const isEditing = editingIndex != null;
      const newRecords = isEditing
        ? existingRecords.map((r, idx) =>
            idx === editingIndex ? { ...r, meterName: meterName.trim(), value: numValue } : r
          )
        : [
            ...existingRecords,
            { meterName: meterName.trim(), value: numValue, source: "manual", addedBy: user?._id },
          ];
      const newTotal = newRecords.reduce((s, r) => s + (r.value || 0), 0);
      const fieldPayload = { records: newRecords, value: newTotal };

      if (item?._id) {
        await waterFormController.updateWaterForm(accessToken, item._id, { [field]: fieldPayload });
      } else {
        const createResponse = await waterFormController.createWaterForm(accessToken, {
          site,
          year,
          period,
          creator_user: user._id,
          date: new Date(),
          [field]: fieldPayload,
        });
        // Condición de carrera: otro request creó el formulario de este
        // período justo antes que nosotros. En vez de perder nuestro
        // registro, lo agregamos al documento que ganó la carrera.
        if (createResponse?.alreadyExisted && createResponse?.waterForm?._id) {
          const winnerRecords = createResponse.waterForm?.[field]?.records || [];
          const mergedRecords = [...winnerRecords, ...newRecords.slice(existingRecords.length)];
          const mergedTotal = mergedRecords.reduce((s, r) => s + (r.value || 0), 0);
          await waterFormController.updateWaterForm(accessToken, createResponse.waterForm._id, {
            [field]: { records: mergedRecords, value: mergedTotal },
          });
        }
      }
      toast.success(isEditing ? (t("meter_record_updated") || "Registro actualizado") : (t("meter_record_added") || "Registro agregado"));
      resetForm();
      // Al editar un registro existente dejamos el modal abierto para poder
      // seguir revisando/editando otros registros del mismo mes.
      if (isEditing) {
        onSavedKeepOpen(fieldPayload);
      } else {
        onSaved();
      }
    } catch (error) {
      console.error(error);
      toast.error(t("error_saving_meter_record") || "Error al guardar el registro");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} size="tiny" className="meter-records-modal">
      <Modal.Header>
        <div className="meter-records-modal__title">
          {(t("meter_records_for") || "Registros de")} {t(period)} — {t(field)}
        </div>
      </Modal.Header>
      <Modal.Content>
        {existingRecords.length > 0 ? (
          <Table basic="very" compact size="small">
            <Table.Body>
              {existingRecords.map((r, i) => (
                <Table.Row key={i} active={editingIndex === i}>
                  {editingIndex === i ? (
                    <>
                      <Table.Cell>
                        <Input
                          size="mini"
                          fluid
                          value={meterName}
                          onChange={(e) => setMeterName(e.target.value)}
                          disabled={saving}
                          autoFocus
                        />
                      </Table.Cell>
                      <Table.Cell textAlign="right">
                        <Input
                          size="mini"
                          type="number"
                          value={value}
                          onChange={(e) => setValue(e.target.value)}
                          disabled={saving}
                          style={{ width: 90 }}
                        />
                      </Table.Cell>
                      {canEdit && (
                        <Table.Cell textAlign="right" collapsing>
                          <Button
                            icon
                            circular
                            color="green"
                            size="mini"
                            className="meter-record-btn"
                            style={{ width: 24, height: 24, minWidth: 24, minHeight: 24, maxWidth: 24, maxHeight: 24, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}
                            onClick={() => handleSave()}
                            loading={saving}
                            disabled={saving}
                            title={t("save_changes") || "Guardar cambios"}
                          >
                            <Icon name="check" style={{ margin: 0 }} />
                          </Button>
                          <Button
                            icon
                            circular
                            size="mini"
                            className="meter-record-btn"
                            style={{ width: 24, height: 24, minWidth: 24, minHeight: 24, maxWidth: 24, maxHeight: 24, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 1, backgroundColor: "#B7B7B7" }}
                            onClick={resetForm}
                            disabled={saving}
                            title={t("cancel_edit") || "Cancelar edición"}
                          >
                            <Icon name="close" style={{ margin: 0, color: "#000" }} />
                          </Button>
                        </Table.Cell>
                      )}
                    </>
                  ) : (
                    <>
                      <Table.Cell>{r.meterName}</Table.Cell>
                      <Table.Cell textAlign="right">{r.value} {currentUnit}</Table.Cell>
                      {canEdit && (
                        <Table.Cell textAlign="right" collapsing>
                          <Button
                            icon
                            circular
                            primary
                            size="mini"
                            className="meter-record-btn"
                            style={{ width: 24, height: 24, minWidth: 24, minHeight: 24, maxWidth: 24, maxHeight: 24, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}
                            onClick={() => handleStartEdit(i)}
                            disabled={saving || editingIndex != null}
                            title={t("edit") || "Editar"}
                          >
                            <Icon name="pencil" style={{ margin: 0 }} />
                          </Button>
                          <Button
                            icon
                            circular
                            color="red"
                            size="mini"
                            className="meter-record-btn"
                            style={{ width: 24, height: 24, minWidth: 24, minHeight: 24, maxWidth: 24, maxHeight: 24, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}
                            onClick={() => handleDelete(i)}
                            disabled={saving || editingIndex != null}
                            title={t("delete") || "Eliminar"}
                          >
                            <Icon name="trash" style={{ margin: 0 }} />
                          </Button>
                        </Table.Cell>
                      )}
                    </>
                  )}
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        ) : (
          <p style={{ color: "#888" }}>
            {currentTotal != null
              ? `${t("current_value") || "Valor actual"}: ${currentTotal} ${currentUnit || ""}`
              : (t("no_records_yet") || "Todavía no hay registros para este mes.")}
          </p>
        )}
        {missingHere.length > 0 && (
          <Message warning size="small" style={{ marginTop: "1em" }}>
            <Icon name="warning sign" />
            {(t("meter_missing_here_prefix") || "Ya está cargado en")} {t(pairField)}, {(t("meter_missing_here_suffix") || "pero no acá")}:{" "}
            <strong>{missingHere.map((r) => r.meterName).join(", ")}</strong>
          </Message>
        )}
        {canEdit && (
          // Se mantiene siempre montado (con visibility, no display:none)
          // aunque se esté editando una fila arriba, para que el modal no
          // cambie de ancho/alto al entrar y salir de edición.
          <div
            style={{
              display: "flex",
              gap: "8px",
              marginTop: "1em",
              alignItems: "center",
              visibility: editingIndex == null ? "visible" : "hidden",
            }}
          >
            <Dropdown
              size="small"
              placeholder={t("meter_name") || "Nombre del medidor"}
              search
              selection
              allowAdditions
              additionLabel={`${t("add_new_meter") || "Nuevo medidor"}: `}
              options={meterOptions.map((name) => ({ key: name, text: name, value: name }))}
              value={editingIndex == null ? meterName : ""}
              onAddItem={(_, d) => setMeterOptions((prev) => [...prev, d.value].sort())}
              onChange={(_, d) => setMeterName(d.value)}
              style={{ flex: 2 }}
            />
            <Input
              size="small"
              type="number"
              placeholder={t("value") || "Valor"}
              value={editingIndex == null ? value : ""}
              onChange={(e) => setValue(e.target.value)}
              style={{ flex: 1 }}
            />
          </div>
        )}
        {!canEdit && (
          <Message size="small" style={{ marginTop: "1em" }}>
            {t("no_permission_add_meter") || "Tu rol no tiene permiso para agregar registros de medidor."}
          </Message>
        )}
      </Modal.Content>
      <Modal.Actions>
        {canEdit && (
          <Button
            className="meter-modal-btn meter-modal-btn--primary"
            style={{ minWidth: 110, visibility: editingIndex == null ? "visible" : "hidden" }}
            onClick={() => handleSave()}
            loading={saving}
            disabled={saving || editingIndex != null}
          >
            <Icon name="plus" />
            {t("add") || "Agregar"}
          </Button>
        )}
        <Button className="meter-modal-btn meter-modal-btn--cancel" style={{ minWidth: 110 }} onClick={onClose} disabled={saving}>
          {t("close") || "Cerrar"}
        </Button>
      </Modal.Actions>
    </Modal>
  );
}

// 🆕 Componente de Celda Editable
function EditableCell({
  field,
  period,
  item,
  editMode,
  selectedUnits,
  editedValues,
  onChange,
  formatNumberWithThousands,
  convertUnits,
}) {
  const key = `${period}_${field}`;
  const isEdited = editedValues[key] !== undefined;

  // Valor para mostrar (sin conversión para mantener unidades fijas)
  const displayValue = isEdited
    ? editedValues[key].value
    : item?.[field]?.value || "";

  const [localValue, setLocalValue] = useState(displayValue);
  const [hasInteracted, setHasInteracted] = useState(false);

  // Actualizar local value cuando cambie displayValue
  useEffect(() => {
    setLocalValue(displayValue);
    setHasInteracted(false);
  }, [displayValue]);

  const handleFocus = (e) => {
    // Si el valor es 0 y el usuario no ha interactuado aún, limpiar el campo
    if (!hasInteracted && (localValue === 0 || localValue === "0")) {
      setLocalValue("");
    }
    setHasInteracted(true);
  };

  const handleChange = (e) => {
    const inputValue = e.target.value;
    setLocalValue(inputValue);

    // Si el campo está vacío, enviar 0
    const newValue = inputValue === "" ? 0 : parseFloat(inputValue) || 0;
    onChange(field, period, item, newValue);
  };

  // Verificar si el campo tiene unidad configurada
  const hasUnit = selectedUnits[field] && selectedUnits[field] !== "";

  // Verificar si la unidad del período difiere de la unidad configurada actual
  const itemUnit = normalizeUnit(item?.[field]?.unit);
  const configuredUnit = normalizeUnit(selectedUnits[field]);
  const isDifferentUnit =
    itemUnit && configuredUnit && itemUnit !== configuredUnit;

  if (!editMode) {
    return (
      <Table.Cell textAlign="right">
        {item && item[field] ? (
          <div>
            {formatNumberWithThousands(item[field].value)}
            {isDifferentUnit && (
              <span
                style={{
                  fontSize: "0.75em",
                  color: "#f57c00",
                  marginLeft: "4px",
                  fontWeight: "bold",
                }}
                title={`Unidad original: ${itemUnit} (actual: ${configuredUnit})`}
              >
                ({itemUnit})
              </span>
            )}
          </div>
        ) : (
          "-"
        )}
      </Table.Cell>
    );
  }

  return (
    <Table.Cell
      textAlign="right"
      style={{
        backgroundColor: isEdited
          ? "#fff9c4"
          : hasUnit
            ? "transparent"
            : "#f5f5f5",
        position: "relative",
        padding: "2px",
      }}
    >
      <input
        type="number"
        step="0.01"
        value={localValue}
        onChange={handleChange}
        onFocus={handleFocus}
        disabled={!hasUnit}
        style={{
          width: "100%",
          border: isEdited ? "2px solid #fbc02d" : "1px solid #ccc",
          borderRadius: "4px",
          padding: "6px 8px",
          textAlign: "right",
          fontSize: "13px",
          cursor: hasUnit ? "text" : "not-allowed",
          backgroundColor: hasUnit ? "white" : "#f5f5f5",
          color: hasUnit ? "inherit" : "#999",
          MozAppearance: "textfield", // Firefox: quitar flechas
        }}
        placeholder="-"
        title={!hasUnit ? "Debe configurar la unidad primero" : ""}
      />
      {isEdited && (
        <Icon
          name="edit"
          size="small"
          style={{
            position: "absolute",
            top: "4px",
            right: "4px",
            color: "#fbc02d",
          }}
        />
      )}
      <style>
        {`
          /* Quitar flechas de input number en todos los navegadores */
          input[type=number]::-webkit-outer-spin-button,
          input[type=number]::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }
          input[type=number] {
            -moz-appearance: textfield;
          }
        `}
      </style>
    </Table.Cell>
  );
}
